import type { SocialLinkingPlatform } from './visibleSocialLinkingPlatforms';
import { isSocialLinkingPlatform } from './visibleSocialLinkingPlatforms';

export type SocialOauthPlatform = SocialLinkingPlatform;

export type SocialOauthCallback =
  | { kind: 'success'; platform: SocialOauthPlatform }
  | {
      kind: 'failure';
      platform: SocialOauthPlatform;
      messageKey:
        | 'account.socialConnectionFailedDescription'
        | 'account.socialOauthErrors.accessDenied'
        | 'account.socialOauthErrors.alreadyConnected'
        | 'account.socialOauthErrors.sessionExpired'
        | 'account.socialOauthErrors.linkedinOrganizationRequired'
        | 'account.socialOauthErrors.facebook_page_required'
        | 'account.socialOauthErrors.facebook_connections_unavailable';
    };

type FailureMessageKey = Extract<SocialOauthCallback, { kind: 'failure' }>['messageKey'];

type OwnDataProperty =
  | { kind: 'missing' }
  | { kind: 'invalid' }
  | { kind: 'value'; value: unknown };

const SAFE_ERROR_MESSAGE_KEYS = new Map<string, FailureMessageKey>([
  ['social_account_already_connected', 'account.socialOauthErrors.alreadyConnected'],
  ['access_denied', 'account.socialOauthErrors.accessDenied'],
  ['user_denied', 'account.socialOauthErrors.accessDenied'],
  ['consent_declined', 'account.socialOauthErrors.accessDenied'],
  ['user_cancelled', 'account.socialOauthErrors.accessDenied'],
  ['invalid_oauth_state', 'account.socialOauthErrors.sessionExpired'],
  ['expired_oauth_state', 'account.socialOauthErrors.sessionExpired'],
  ['oauth_platform_mismatch', 'account.socialOauthErrors.sessionExpired'],
  ['linkedin_organization_required', 'account.socialOauthErrors.linkedinOrganizationRequired'],
  ['facebook_page_required', 'account.socialOauthErrors.facebook_page_required'],
  ['facebook_connections_unavailable', 'account.socialOauthErrors.facebook_connections_unavailable'],
]);

function ownDataProperty(value: object, key: PropertyKey): OwnDataProperty {
  try {
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    if (!descriptor) return { kind: 'missing' };
    if (!Object.prototype.hasOwnProperty.call(descriptor, 'value')) {
      return { kind: 'invalid' };
    }
    return { kind: 'value', value: descriptor.value };
  } catch {
    return { kind: 'invalid' };
  }
}

function isSocialOauthPlatform(value: unknown): value is SocialOauthPlatform {
  return isSocialLinkingPlatform(value);
}

function safeErrorMessageKey(error: unknown, reason: unknown) {
  if (typeof error === 'string') {
    const errorKey = SAFE_ERROR_MESSAGE_KEYS.get(error);
    if (errorKey) return errorKey;
  }
  if (typeof reason === 'string') {
    const reasonKey = SAFE_ERROR_MESSAGE_KEYS.get(reason);
    if (reasonKey) return reasonKey;
  }
  return 'account.socialConnectionFailedDescription' as const;
}

/** Resolves the one trusted callback origin. Missing, relative, malformed, and
 * non-HTTP(S) configuration deliberately yields no trusted origin. */
export function resolveConfiguredApiOrigin(apiUrl: unknown): string | null {
  if (typeof apiUrl !== 'string' || apiUrl.trim() === '') return null;
  try {
    const url = new URL(apiUrl);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    return url.origin;
  } catch {
    return null;
  }
}

/** Reads only own data properties from the public OAuth callback envelope.
 * Accessors, arrays, malformed values, and hostile/revoked proxies fail closed.
 * Provider text is reduced to a small localized allowlist before reaching UI. */
export function parseSocialOauthCallbackMessage(data: unknown): SocialOauthCallback | null {
  if (data === null || typeof data !== 'object') return null;
  try {
    if (Array.isArray(data)) return null;
  } catch {
    return null;
  }

  const type = ownDataProperty(data, 'type');
  const platform = ownDataProperty(data, 'platform');
  const success = ownDataProperty(data, 'success');
  if (
    type.kind !== 'value'
    || type.value !== 'autoclipper:social-connection'
    || platform.kind !== 'value'
    || !isSocialOauthPlatform(platform.value)
    || success.kind !== 'value'
    || typeof success.value !== 'boolean'
  ) {
    return null;
  }

  const error = ownDataProperty(data, 'error');
  const reason = ownDataProperty(data, 'reason');
  if (error.kind === 'invalid' || reason.kind === 'invalid') return null;
  if (success.value) return { kind: 'success', platform: platform.value };

  return {
    kind: 'failure',
    platform: platform.value,
    messageKey: safeErrorMessageKey(
      error.kind === 'value' ? error.value : undefined,
      reason.kind === 'value' ? reason.value : undefined,
    ),
  };
}
